import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { ShieldCheck, MessageSquareText, Database, UserCheck, Mail } from "lucide-react";

const Section = ({ icon: Icon, title, accent, children, testId }) => (
  <section data-testid={testId} className="glass rounded-2xl p-6 border border-white/10">
    <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
      <Icon className={`w-5 h-5 ${accent}`} /> {title}
    </h2>
    <div className="space-y-2 text-sm text-gray-300 leading-relaxed">{children}</div>
  </section>
);

export const SMS_CONSENT_EN = "By entering your number you agree to receive account and security text messages from PhonePlay (one-time passcodes, login verification and account activity alerts). Message frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.";

export default function Privacy() {
  useSeo({ title: "Privacy Policy", path: "/privacy", description: "How PhonePlay (Phone Play LLC) collects, uses and protects your information, including our SMS program and the data we never share." });
  return (
    <div data-testid="privacy-page">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-8 h-8 text-cyan-400" />
        <h1 className="font-display text-3xl sm:text-4xl font-black">Privacy Policy</h1>
      </div>
      <p className="text-gray-400 mb-8 text-sm">How PhonePlay collects, uses and protects your information. Last updated June 2026.</p>

      <div className="space-y-5">
        <Section icon={Database} title="1. Information We Collect" accent="text-purple-400">
          <p>Account details you provide (email, display name, date of birth, state/country), an optional mobile phone number,
          gameplay and contest results, wallet balances and transaction ledgers, and technical data such as IP address and
          approximate location used for eligibility checks. Payment card details are handled by Stripe and never stored by PhonePlay.</p>
        </Section>

        <Section icon={UserCheck} title="2. How We Use It" accent="text-emerald-400">
          <ul className="list-disc list-inside space-y-1 text-gray-400">
            <li>Operate your account, Fun Coin balance and skill games.</li>
            <li>Verify age (18+) and keep one account per person.</li>
            <li>Prevent fraud, collusion and abuse; keep append-only audit ledgers.</li>
            <li>Send transactional account and security communications (see Section 3).</li>
          </ul>
        </Section>

        <Section icon={MessageSquareText} title="3. SMS / Text Message Communications" accent="text-cyan-400" testId="privacy-sms-section">
          <p><span className="font-semibold text-gray-100">Opt-in.</span> You may voluntarily add your mobile number in the
          "Verify your phone" step after sign-up or in Wallet → Phone &amp; Two-Factor Security. Consent is given by entering
          your number and confirming the one-time code we text you. Phone numbers are never required to play free games.</p>
          <p><span className="font-semibold text-gray-100">What we send.</span> Transactional messages only: one-time
          passcodes for phone verification and two-factor login, and account activity alerts (for example, a sign-in from a
          new device or a status update on a request you made in your account). We do not send marketing or promotional texts.</p>
          <p><span className="font-semibold text-gray-100">Consent language shown at collection:</span></p>
          <p data-testid="privacy-sms-consent" className="rounded-xl bg-cyan-500/10 border border-cyan-500/25 px-4 py-3 text-cyan-100 text-xs">{SMS_CONSENT_EN}</p>
          <p><span className="font-semibold text-gray-100">Opt-out.</span> Reply <span className="font-mono">STOP</span> to any
          message to unsubscribe, or remove your phone number in Wallet. Reply <span className="font-mono">HELP</span> for help
          or email <a href="mailto:support@phoneplay.net" className="text-cyan-300 hover:underline">support@phoneplay.net</a>.
          Message frequency varies; message and data rates may apply. Carriers are not liable for delayed or undelivered messages.</p>
          <p data-testid="privacy-no-share" className="font-semibold text-gray-100">No mobile information will be shared with third parties or affiliates for
          marketing or promotional purposes. All the above categories exclude text messaging originator opt-in data and consent; this information
          will not be shared with any third parties.</p>
        </Section>

        <Section icon={ShieldCheck} title="4. Sharing &amp; Security" accent="text-amber-400">
          <p>We never sell personal information. We share data only with the service providers needed to run PhonePlay (payment
          processing and identity verification), with authorities when legally required, and to prevent fraud. <span className="font-semibold text-gray-100">Mobile phone numbers
          and text-messaging opt-in data and consent are excluded from this sharing and are not shared with any third parties or affiliates.</span> Data is
          encrypted in transit and access is restricted and logged.</p>
        </Section>

        <Section icon={Mail} title="5. Your Choices &amp; Contact" accent="text-purple-400">
          <p>You can update your details, remove your phone number, set spend limits or self-exclude in the app, and request
          account deletion by emailing <a href="mailto:support@phoneplay.net" className="text-cyan-300 hover:underline">support@phoneplay.net</a>.
          PhonePlay is for adults 18+; we do not knowingly collect data from minors.</p>
          <p className="text-gray-500 text-xs">See also our <Link to="/terms" className="text-cyan-300 hover:underline">Terms &amp; Rules</Link> and <Link to="/responsible-gaming" className="text-cyan-300 hover:underline">Responsible Gaming</Link> pages.</p>
        </Section>
      </div>
    </div>
  );
}
